import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import type { ProjectPermissionKey, ProjectRoleKey } from "@/lib/project-permissions";

// VIB-1164: nøkkel-/rolletyper eies nå av skjema-modulen
// (`@/lib/project-permissions`). Re-eksporteres her for bakoverkompatibilitet
// med eksisterende imports.
export type { ProjectPermissionKey, ProjectRoleKey };

export interface ProjectRolePermission {
  role: ProjectRoleKey;
  permission_key: ProjectPermissionKey;
  granted: boolean;
}

export function useProjectRolePermissions() {
  return useQuery({
    queryKey: ["project-role-permissions"],
    queryFn: async (): Promise<ProjectRolePermission[]> => {
      const { data, error } = await supabase
        .from("project_role_permissions")
        .select("role, permission_key, granted");
      if (error) throw error;
      return (data ?? []) as ProjectRolePermission[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateProjectRolePermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      role: ProjectRoleKey;
      permission_key: ProjectPermissionKey;
      granted: boolean;
    }) => {
      const { error } = await supabase
        .from("project_role_permissions")
        .upsert(
          { ...input, updated_at: new Date().toISOString() },
          { onConflict: "role,permission_key" },
        );
      if (error) throw error;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "project_role_permissions");
    },
  });
}
